import type { RequestStatus } from '@gestion-permisos/contracts';
import { Link } from 'react-router-dom';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { CARD_SURFACE } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { COUNT_LABELS, REQUEST_STATUSES } from '../requests/format.js';
import { useRequestCount } from '../requests/requests-api.js';

/**
 * Secciones, en el orden de las pestañas de la Power App (`src/components/admin-workspace.tsx`). Calendario y
 * Auditoría no son pestañas allí, pero aquí son pantallas reales y siguen siendo alcanzables. Sin
 * «Capacidades»: los roles se asignan en el empleado.
 */
const SECTIONS = [
  { to: '/administracion/solicitudes', title: 'Solicitudes', description: 'Todas las solicitudes: cancelar, duración real y reasignación del aprobador.' },
  { to: '/analitica', title: 'Analítica', description: 'Horas aprobadas, tiempos de decisión y exportación a Excel.' },
  { to: '/administracion/empleados', title: 'Empleados', description: 'Altas, datos, roles y revocación de sesiones.' },
  { to: '/administracion/jerarquia', title: 'Jerarquía', description: 'Árbol de responsables y problemas señalados.' },
  { to: '/administracion/areas', title: 'Áreas', description: 'Crear, editar y desactivar áreas.' },
  { to: '/administracion/tipos', title: 'Tipos de permiso', description: 'Crear, editar y desactivar tipos de permiso.' },
  { to: '/administracion/calendario', title: 'Calendario', description: 'Jornada, tramos y festivos.' },
  { to: '/administracion/auditoria', title: 'Auditoría', description: 'Eventos de auditoría (solo lectura).' },
];

/**
 * Índice de administración (W7 a W10), con el aspecto de la Power App (`src/pages/administration-page.tsx`):
 * cabecera, un conteo por estado de todas las solicitudes y las secciones como pestañas-enlace. Cada conteo
 * es una página de un elemento (`scope=all`, `pageSize=1`) de la que solo se usa `total`: nunca se descarga
 * la lista. El índice no decide quién es administrador: si un conteo responde 403, se muestra una vez.
 */
export function AdminIndexPage() {
  // Un conteo por estado, en el orden del flujo (`REQUEST_STATUSES`); un hook por línea, sin bucles.
  const results: Record<RequestStatus, ReturnType<typeof useRequestCount>> = {
    DRAFT: useRequestCount('all', 'DRAFT'),
    PENDING_APPROVAL: useRequestCount('all', 'PENDING_APPROVAL'),
    APPROVED: useRequestCount('all', 'APPROVED'),
    REJECTED: useRequestCount('all', 'REJECTED'),
    CANCELLED: useRequestCount('all', 'CANCELLED'),
  };
  const counts = REQUEST_STATUSES.map((status) => ({ status, result: results[status] }));
  const errors = counts.map(({ result }) => result.error).filter((error) => error !== null);
  const denied = errors.find((error) => error instanceof ApiProblem && error.status === 403);
  if (denied) return <ApiErrorView error={denied} />;
  const failure = errors[0];

  return (
    <div className="space-y-7 lg:px-6 lg:py-4">
      <PageHeader title="Administración" description="Gestión global de solicitudes, estructura organizativa y catálogos." />
      <section aria-labelledby="conteos-por-estado">
        <h2 id="conteos-por-estado" className="sr-only">
          Solicitudes por estado
        </h2>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {counts.map(({ status, result }) => (
            <StatusCount key={status} status={status} result={result} />
          ))}
        </ul>
        {failure !== undefined ? (
          <div className="mt-4">
            <ApiErrorView error={failure} />
          </div>
        ) : null}
      </section>
      <ul aria-label="Secciones de administración" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {SECTIONS.map((section) => (
          <li key={section.to}>
            <Link
              to={section.to}
              className={`block h-full p-4 transition-colors hover:border-ring hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none ${CARD_SURFACE}`}
            >
              <span className="block font-medium">{section.title}</span>
              <span className="mt-1 block text-sm text-muted-foreground">{section.description}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusCount({ status, result }: { status: RequestStatus; result: { isPending: boolean; isError: boolean; data: number | undefined } }) {
  let value = '…';
  if (result.isError) value = '—';
  else if (result.data !== undefined) value = String(result.data);
  return (
    <li aria-label={`${COUNT_LABELS[status]}: ${value}`} className={`p-5 ${CARD_SURFACE}`}>
      <span aria-hidden="true" className="block text-sm text-muted-foreground">
        {COUNT_LABELS[status]}
      </span>
      <span aria-hidden="true" className="mt-2 block text-3xl font-semibold tracking-tight">
        {value}
      </span>
    </li>
  );
}
